import { createTheme } from '@mui/material/styles';

import * as MuiAccordion from './overrides/accordion/accordion';
import { Button } from './models/button';
import { Chip } from './models/chip';
import { CloseIcon } from './icons';
import { General } from './models/general';
import { Modal } from './models/modal';
import { MuiAutocomplete } from './overrides/autocomplete/autocomplete';
import { MuiDatePicker } from './overrides/date-picker/date-picker';
import { MuiFormHelperText } from './overrides/form-helper-text/form-helper-text';
import { MuiPagination, MuiPaginationItem } from './overrides/table/pagination';
import { MuiRating } from './overrides/rating/rating';
import { Scrollbars } from './models/scrollbar';
import { SearchField } from './models/search-field';
import { Sidebar } from './models/sidebar';
import { Status } from './models/status';
import { Stepper } from './models/stepper';
import { TableFooter } from './models/table-footer';
import { Toolbar } from './models/toolbar';
import { classes as muiInputAdornmentClassesOverrides } from './overrides/input/input-adornment';
import { classes as muiInputClassesOverrides } from './overrides/input/input';
import { classes as muiOutlinedInputClassesOverrides } from './overrides/input/outlined-input';
import { classes as muiTableCellClassesOverrides } from './overrides/table/table-cell';
import { classes as muiTablePaginationClassesOverrides } from './overrides/table/table-pagination';
import { classes as muiTableRowClassesOverrides } from './overrides/table/table-row';
import { typography } from './typography';

declare module '@mui/material/styles' {
    interface Palette {
        alertInfo: Palette['primary'];
        alertSuccess: Palette['primary'];
        alertWarning: Palette['primary'];
        alertError: Palette['primary'];
    }

    interface PaletteOptions {
        alertInfo?: PaletteOptions['primary'];
        alertSuccess?: PaletteOptions['primary'];
        alertWarning?: PaletteOptions['primary'];
        alertError?: PaletteOptions['primary'];
    }

    interface Theme {
        // old theme
        general: General;
        scrollbars: Scrollbars;
        stepper: Stepper;
        sidebar: Sidebar;
        modal: Modal;
        button: Button;
        chip: Chip;
        searchField: SearchField;
        tableFooter: TableFooter;
        toolbar: Toolbar;
        status: Status;
        // new theme
    }
    // allow configuration using `createMuiTheme`
    interface ThemeOptions {
        // old theme
        general?: General;
        scrollbars?: Scrollbars;
        stepper?: Stepper;
        sidebar?: Sidebar;
        modal?: Modal;
        button?: Button;
        chip?: Chip;
        searchField?: SearchField;
        tableFooter?: TableFooter;
        toolbar?: Toolbar;
        status?: Status;
        // new theme
    }
}

export const theme = createTheme({
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    height: '100%',
                    color: '#21272E',
                    background: '#FAFAFA',
                    fontFeatureSettings: '"ss01"',
                },
                /* Override Material UI defaults */
                body: {
                    fontSize: 15,
                    height: '100%',
                    scrollbarColor: '#2C93F0 transparent', // FireFox
                    '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
                        width: 5,
                    },
                    '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
                        borderRadius: 2.5,
                        backgroundColor: '#2C93F0',
                    },
                    '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
                        backgroundColor: '#D5DFEB',
                        borderRadius: 2.5,
                    },
                },
            },
        },
        MuiInputAdornment: muiInputAdornmentClassesOverrides,
        MuiInput: muiInputClassesOverrides,
        MuiInputBase: muiInputClassesOverrides,
        MuiOutlinedInput: muiOutlinedInputClassesOverrides,
        MuiPaper: {
            styleOverrides: {
                root: {
                    marginBottom: '3px',
                    color: '#21272E',
                },
                elevation1: {
                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.12), inset 0px -1px 0px #F1F1F1',
                },
            },
        },
        MuiTableCell: muiTableCellClassesOverrides,
        MuiTablePagination: muiTablePaginationClassesOverrides,
        MuiTableRow: muiTableRowClassesOverrides,
        MuiTypography: {
            styleOverrides: {
                h5: {
                    fontSize: '26px',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    paddingRight: '64px !important',
                    height: 22,
                },
            },
        },
        MuiList: {
            styleOverrides: {
                padding: {
                    paddingTop: 4,
                    paddingBottom: 4,
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                root: {
                    flex: '1 1 auto',
                    minWidth: 0,
                    marginTop: 4,
                    marginBottom: 4,
                    padding: '0 15px',
                },
                inset: {
                    paddingLeft: 56,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    paddingTop: 12,
                    paddingBottom: 12,
                    height: 48,
                },
                gutters: {
                    paddingLeft: 24,
                    paddingRight: 32,
                },
            },
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: 0,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: 12,
                    '& svg': {
                        width: '1.25rem',
                        height: '1.25rem',
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                contained: {
                    '&$disabled': {
                        opacity: 0.5,
                        color: undefined,
                        backgroundColor: undefined,
                        boxShadow: undefined,
                    },
                },
                root: {
                    whiteSpace: 'nowrap',
                    '&$disabled': {
                        color: undefined,
                    },
                    fontSize: 13,
                    lineHeight: '150%',
                    fontWeight: 400,
                },
                startIcon: {
                    marginLeft: 0,
                    marginRight: 8,
                },
                endIcon: {
                    marginLeft: 16,
                    marginRight: 0,
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '6px 88px 40px',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: 0,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paperWidthFalse: {
                    minWidth: 600,
                },
                paper: {
                    maxHeight: 'calc(100% - 144px)',
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                colorPrimary: {
                    backgroundColor: '#F2F2F2',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                textColorInherit: {
                    opacity: 1,
                },
                root: {
                    fontSize: 15,
                    minWidth: 160,
                    lineHeight: '145%',
                    fontWeight: 400,
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                colorPrimary: {
                    color: '#2196F3',
                },
                fontSizeInherit: {
                    fontSize: 20,
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                colorSecondary: {
                    backgroundColor: '#F94264',
                },
            },
        },
        MuiAutocomplete,
        MuiSwitch: {
            styleOverrides: {
                track: {
                    opacity: 1,
                    backgroundColor: '#C8C9CB',
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#FFF',
                    border: '1.5px solid #fff',
                },
                // Style is applied if no img is present
                colorDefault: {
                    backgroundColor: '#21272E',
                    fontSize: 19,
                    letterSpacing: 1,
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                root: {
                    fontWeight: 400,
                    '&.Mui-completed': {
                        fontWeight: 400,
                    },
                    '&.Mui-active': {
                        fontWeight: 600,
                    },
                },
                label: {
                    fontSize: 13,
                    lineHeight: '150%',
                    fontWeight: 400,

                    '&.MuiStepLabel-active': {
                        fontWeight: 600,
                    },
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: 13,
                    lineHeight: '150%',
                    fontWeight: 400,
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    backgroundColor: '#373D43',
                    color: '#fff',
                    fontSize: 12,
                    lineHeight: '145%',
                    fontWeight: 400,
                    padding: '7.5px 16px',
                    fontStyle: 'normal',
                    letterSpacing: 0,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    height: 48,
                    minWidth: 232,
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                colorSuccess: {
                    backgroundColor: '#7ED321',
                },
                colorError: {
                    backgroundColor: '#F94264',
                },
                colorInfo: {
                    color: '#000',
                    backgroundColor: '#EEEFEF',
                },
            },
        },
        MuiPagination,
        MuiPaginationItem,
        MuiDatePicker,
        ...MuiAccordion,
        MuiFormHelperText,
        MuiRating,
        MuiAlert: {
            styleOverrides: {
                root: ({ theme }) => ({
                    padding: theme.spacing(1, 2),
                    color: theme.palette.text.primary,
                    alignItems: 'center',
                }),
                icon: {
                    padding: 0,

                    svg: {
                        width: 20,
                        height: 20,
                    },
                },
                message: {
                    padding: 0,
                },
                action: ({ theme }) => ({
                    padding: 0,

                    button: {
                        svg: {
                            color: theme.palette.text.primary,
                        },
                    },
                }),
                filled: {
                    borderRadius: 0,
                },
                filledInfo: ({ theme }) => ({
                    backgroundColor: theme.palette.alertInfo.light,
                }),
                filledSuccess: ({ theme }) => ({
                    backgroundColor: theme.palette.alertSuccess.light,
                }),
                filledWarning: ({ theme }) => ({
                    backgroundColor: theme.palette.alertWarning.light,
                }),
                filledError: ({ theme }) => ({
                    backgroundColor: theme.palette.alertError.light,
                }),
                outlinedInfo: ({ theme }) => ({
                    borderColor: theme.palette.alertInfo.main,
                    backgroundColor: theme.palette.alertInfo.light,
                }),
                outlinedSuccess: ({ theme }) => ({
                    borderColor: theme.palette.alertSuccess.main,
                    backgroundColor: theme.palette.alertSuccess.light,
                }),
                outlinedWarning: ({ theme }) => ({
                    borderColor: theme.palette.alertWarning.main,
                    backgroundColor: theme.palette.alertWarning.light,
                }),
                outlinedError: ({ theme }) => ({
                    borderColor: theme.palette.alertError.main,
                    backgroundColor: theme.palette.alertError.light,
                }),
            },
            defaultProps: {
                components: {
                    CloseIcon: CloseIcon,
                },
            },
        },
        MuiAlertTitle: {
            styleOverrides: {
                root: ({ theme }) => ({
                    ...theme.typography.body2,
                    fontWeight: 600,
                    margin: 0,
                }),
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: ({ theme }) => ({
                    color: theme.palette.text.primary,
                }),
            },
        },
    },
    palette: {
        primary: {
            main: '#1565D8', // --accent
            light: '#42ACC1', // teal top bar — kept intentionally
            dark: '#0F4FAD', // --accent-hi
        },
        secondary: {
            main: '#f50057',
        },
        action: {
            disabledBackground: 'rgba(153, 153, 153, .1)',
        },
        text: {
            primary: '#1A2027', // --ink
            secondary: 'rgba(33, 39, 46, .8)',
        },
        background: {
            default: '#F4F6F8', // --canvas
        },
        grey: {
            200: '#EEEFEF',
            300: '#DEDFE0',
            400: '#C8C9CB',
            500: '#A7A9AC',
            700: '#909397',
            800: '#6F7377',
            900: '#373D43',
        },
        error: {
            main: '#D5004A', // --crit
        },
        common: {
            white: '#FFF',
        },
        success: {
            main: '#2E7D32', // --ok
        },
        alertInfo: {
            main: '#B9D4F7', // --accent-line
            light: '#E8F1FD', // --accent-soft
        },
        alertSuccess: {
            main: '#B0E972',
            light: '#E7F4E8', // --ok-soft
        },
        alertWarning: {
            main: '#FCCE92',
            light: '#FDF1E0', // --warn-soft
        },
        alertError: {
            main: '#FFADCA',
            light: '#FDE7EE', // --crit-soft
        },
    },
    typography,

    // Custom variables.

    status: {
        default: {
            color: 'rgba(33, 39, 46, .8)',
            backgroundColor: '#F1F1F1',
        },
        error: {
            color: '#FFF',
            backgroundColor: '#D5004A', // --crit
        },
        primary: {
            color: '#FFF',
            backgroundColor: '#1565D8', // --accent
        },
        success: {
            color: '#FFF',
            backgroundColor: '#2E7D32', // --ok
        },
        warning: {
            color: '#FFF',
            backgroundColor: '#E8850F', // --warn
        },
        info: {
            color: '#FFF',
            backgroundColor: '#1565D8', // --accent
        },
    },
    general: {
        pageContentSpacing: '62px',
        colors: {
            activitySidebarBackground: '#F7F7F8',
            black: '#1A2027', // --ink
            hint: 'rgba(0, 0, 0, 0.38)',
        },
        tableGutter: {
            outer: '24px',
            inner: '13px',
        },
    },
    stepper: {
        height: '72px',
        progressBarSize: '120px',
    },
    searchField: {
        borderRadius: '24px',
        minWidth: '230px',
        paddingLeft: 18,
        paddingBlock: 9,
        fontSize: 13,
    },
    tableFooter: {
        borderTop: '1px solid #6F7377',
        background: 'inherit',
        paddingBlock: 2,
        paddingInline: 3,
        fontSize: 13,
        controlHeight: 35,
    },
    button: {
        minHeight: 38,
        borderRadius: 4,
        fontSize: 13,
    },
    chip: {
        height: '1.5rem',
        borderRadius: '24px',
        fontSize: 12,
        fontWeight: 600,
        letterSpacing: '0.5px',
        labelMinWidth: 120,
        listTextTransform: 'uppercase' as const,
        listTextAlign: 'center' as const,
        listWidth: '100%' as const,
    },
    modal: {
        paperBorderRadius: '4px',
        paperBoxShadow:
            '0px 11px 15px -7px rgba(0,0,0,0.2), 0px 24px 38px 3px rgba(0,0,0,0.14), 0px 9px 46px 8px rgba(0,0,0,0.12)',
        titleAlign: 'center' as const,
        titlePadding: '35px 0',
        titleFontSize: '20px',
        titleFontWeight: 400,
        titleBorderBottom: 'none',
        closeSize: '2.5rem',
        closeColor: '#1565D8',
        closeOffset: '12px',
        messageAlign: 'center' as const,
        messageFontSize: 15,
        messageLineHeight: '22px',
        contentPadding: '20px 24px',
        actionsPadding: '24px 88px',
        actionsBorderTop: 'none',
        actionTextTransform: 'uppercase' as const,
        actionFontWeight: 600,
        actionLetterSpacing: '0.5px',
    },
    toolbar: {
        height: '48px',
        backgroundColor: '#42ACC1', // the teal bar, unchanged
        color: '#FFFFFF',
        disabledColor: '#7DC7D4',
        hoverBackgroundColor: '#6DBFCF',
    },
    sidebar: {
        logoWidthOpen: 81,
        logoWidthCollapsed: 40,
        widthOpen: 248,
        widthCollapsed: 50,
        iconSize: 20,
        subheaderSize: 13,
        linkItemSize: 15,
        palette: {
            background: '#23282F',
            subheader: '#4A4F55',
            divider: '#46494F',
            text: '#a6a9ab',
            activeLink: '#fff',
        },
    },
    scrollbars: {
        color: '#2C93F0',
        backgroundColor: '#D5DFEB',
        backgroundColorDark: '#171B21',
        borderRadius: 2.5,
        width: 5,
    },
});
